<template>
  <span>
    <b-button
      v-if="show_button"
      size="sm"
      :class="`btn-ai-quick ${button_class}`"
      @click="open"
    >
      <img src="@/assets/icon-ai.svg" alt="" class="kl-ai-icon-sm mr-1" />
      {{ $t("reusable.aiQuestionsBuilderModal.openButton") }}
    </b-button>

    <!-- `ignore-enforce-focus-selector` y no `no-enforce-focus`: es lo que deja
         escribir en la barra de IA lateral sin que el modal robe el foco. -->
    <b-modal
      v-model="visible"
      size="xl"
      dialog-class="kl-ai-dialog"
      body-class="kl-ai-body"
      footer-class="kl-ai-footer"
      ignore-enforce-focus-selector="#sidebar-right"
      :no-close-on-backdrop="busy"
      :no-close-on-esc="busy"
      @hide="confirmClose"
      @hidden="reset"
    >
      <template #modal-header>
        <div class="d-flex w-100 flex-column">
          <div class="d-flex align-items-start justify-content-between">
            <div class="d-flex align-items-start">
              <img
                src="@/assets/icon-ai.svg"
                alt=""
                class="kl-ai-icon-lg mr-2"
              />
              <div>
                <h5 class="kl-ai-title mb-0">
                  {{
                    $t("reusable.aiQuestionsBuilderModal.title", {
                      entity: questionsLabel,
                    })
                  }}
                </h5>
                <small class="text-muted">
                  {{
                    step === "setup"
                      ? $t("reusable.aiQuestionsBuilderModal.subtitleSetup")
                      : $t("reusable.aiQuestionsBuilderModal.subtitleReview")
                  }}
                </small>
              </div>
            </div>
            <b-button
              v-if="!busy"
              variant="link"
              size="sm"
              class="kl-ai-close p-0"
              :aria-label="$t('reusable.aiQuestionsBuilderModal.close')"
              @click="close"
            >
              <b-icon icon="x" font-scale="1.4"></b-icon>
            </b-button>
          </div>
          <!-- Indicador de paso: dos tramos, no números. Con dos pasos un
               stepper numerado pesa más de lo que informa. -->
          <div class="kl-ai-steps mt-2" aria-hidden="true">
            <span class="kl-ai-step kl-ai-step-done"></span>
            <span
              class="kl-ai-step"
              :class="{ 'kl-ai-step-done': step === 'review' }"
            ></span>
          </div>
        </div>
      </template>

      <b-alert :show="error != null" variant="danger" class="py-2">
        <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
        {{ error }}
      </b-alert>

      <!-- ===================== Paso 1 · El plan ===================== -->
      <!-- El plan es el ÚNICO input que decide qué se crea: una fila por
           pregunta, con su Indicador y su tipo. No se le pregunta a la IA
           "cuántas quieres": contar es justo lo que hace mal. -->
      <template v-if="step === 'setup'">
        <div class="kl-ai-context mb-3">
          <div v-if="pieceName" class="kl-ai-small">
            <b-icon icon="diagram-3" class="mr-1"></b-icon>
            {{
              $t("reusable.aiQuestionsBuilderModal.fromDesign", {
                piece: pieceName,
              })
            }}
          </div>
          <div class="kl-ai-small text-muted">
            {{ instrumentLabel }}: <strong>{{ testTitle }}</strong>
            <template v-if="situationName">
              · {{ situationName }}
            </template>
            <template v-if="testScore > 0">
              ·
              {{
                $t("reusable.aiQuestionsBuilderModal.testScore", {
                  score: testScore,
                })
              }}
            </template>
          </div>
        </div>

        <label class="kl-ai-label kl-ai-small">
          {{ $t("reusable.aiQuestionsBuilderModal.themeLabel") }}
        </label>
        <AiThemeInput
          v-model="theme"
          class="mb-3"
          :placeholder="$t('reusable.aiQuestionsBuilderModal.themePlaceholder')"
          :hint="$t('reusable.aiQuestionsBuilderModal.themeHint')"
          :info="$t('reusable.aiQuestionsBuilderModal.themeInfo')"
          :disabled="busy"
        ></AiThemeInput>

        <!-- Lo que el instrumento YA tiene. Sin esto, reabrir el modal proponía
             otra vez una pregunta por Indicador y duplicaba lo ya creado sin que
             nada lo dijera. -->
        <div v-if="existingCount > 0" class="kl-ai-existing mb-3">
          <div class="kl-ai-label kl-ai-small mb-1">
            {{
              $t("reusable.aiQuestionsBuilderModal.existingTitle", {
                count: existingCount,
                score: existingScore,
              })
            }}
          </div>
          <div class="kl-ai-small">
            <span
              v-for="coverage in criteriaCoverage"
              :key="`coverage-${coverage.id}`"
              class="kl-ai-chip"
              :class="{ 'kl-ai-chip-empty': coverage.count === 0 }"
            >
              {{ coverage.prefix }} ·
              <template v-if="coverage.count > 0">
                {{
                  $t("reusable.aiQuestionsBuilderModal.coverageCount", {
                    count: coverage.count,
                  })
                }}
              </template>
              <template v-else>
                {{ $t("reusable.aiQuestionsBuilderModal.coverageNone") }}
              </template>
            </span>
          </div>
        </div>

        <div class="kl-ai-label mb-1">
          {{
            $t("reusable.aiQuestionsBuilderModal.planTitle", {
              entity: questionsLabel,
            })
          }}
        </div>
        <p class="kl-ai-small text-muted mb-2">
          {{
            $t("reusable.aiQuestionsBuilderModal.planHint", {
              entity: criteriaLabel,
            })
          }}
        </p>

        <b-table-simple small bordered responsive class="kl-ai-table mb-1">
          <b-thead>
            <b-tr>
              <b-th style="width: 42%">
                {{ dimensionDriven ? dimensionLabel : criteriaLabel }}
              </b-th>
              <b-th style="width: 22%">
                {{ $t("reusable.aiQuestionsBuilderModal.colType") }}
              </b-th>
              <b-th style="width: 11%">
                {{ $t("reusable.aiQuestionsBuilderModal.colOptions") }}
              </b-th>
              <b-th style="width: 11%">
                {{ $t("reusable.aiQuestionsBuilderModal.colCorrect") }}
              </b-th>
              <b-th style="width: 9%">
                {{ $t("reusable.aiQuestionsBuilderModal.colScore") }}
              </b-th>
              <b-th style="width: 5%"></b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="row in plan" :key="row.uid">
              <b-td>
                <!-- Donde se evalúa a través de la Dimensión, la pregunta se
                     cuelga de una dimensión de la pauta y no declara
                     Indicadores: el Indicador lo aporta la dimensión. -->
                <b-form-select
                  v-if="dimensionDriven"
                  v-model="row.dimension"
                  size="sm"
                  :disabled="busy"
                >
                  <b-form-select-option
                    v-for="dimension in dimensionOptions"
                    :key="`dimension-${row.uid}-${dimension.id}`"
                    :value="dimension.id"
                  >
                    {{ dimension.label }}
                  </b-form-select-option>
                </b-form-select>
                <!-- Varios Indicadores por pregunta: el modelo lo permite (es un
                     m2m) y una pregunta integradora los necesita. Se eligen con
                     Ctrl o Shift, igual que en los demás selectores múltiples de
                     la plataforma. -->
                <b-form-select
                  v-else
                  v-model="row.criterias"
                  size="sm"
                  multiple
                  :select-size="criteriaSelectSize"
                  :disabled="busy"
                >
                  <b-form-select-option-group
                    v-for="group in criteriaGroups"
                    :key="`group-${group.id}`"
                    :label="group.label"
                  >
                    <b-form-select-option
                      v-for="criteria in group.criterias"
                      :key="`criteria-${criteria.id}`"
                      :value="criteria.id"
                    >
                      {{ criteria.label }}
                    </b-form-select-option>
                  </b-form-select-option-group>
                </b-form-select>
              </b-td>
              <b-td>
                <b-form-select
                  v-model="row.type"
                  size="sm"
                  :options="typeOptions"
                  :disabled="busy"
                ></b-form-select>
              </b-td>
              <b-td>
                <b-form-input
                  v-if="isSelectionType(row.type)"
                  v-model.number="row.options_count"
                  type="number"
                  size="sm"
                  min="3"
                  max="6"
                  step="1"
                  :disabled="busy"
                ></b-form-input>
                <span v-else class="kl-ai-small text-muted">—</span>
              </b-td>
              <b-td>
                <!-- Sólo la Selección Múltiple lo elige: la Simple tiene una por
                     definición. El tope deja siempre un distractor, porque una
                     pregunta en la que todo es correcto no discrimina nada. -->
                <b-form-input
                  v-if="row.type === 'multiple_selection'"
                  v-model.number="row.correct_count"
                  type="number"
                  size="sm"
                  min="2"
                  :max="Math.max(2, (row.options_count || 4) - 1)"
                  step="1"
                  :disabled="busy"
                ></b-form-input>
                <span
                  v-else-if="isSelectionType(row.type)"
                  class="kl-ai-small text-muted"
                >
                  1
                </span>
                <span v-else class="kl-ai-small text-muted">—</span>
              </b-td>
              <b-td>
                <b-form-input
                  v-model.number="row.max_score"
                  type="number"
                  size="sm"
                  min="0.5"
                  step="0.5"
                  :disabled="busy"
                ></b-form-input>
              </b-td>
              <b-td class="text-center">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 kl-ai-row-remove"
                  :disabled="busy"
                  :title="$t('reusable.aiQuestionsBuilderModal.removeRow')"
                  @click="removeRow(row.uid)"
                >
                  <b-icon icon="trash"></b-icon>
                </b-button>
              </b-td>
            </b-tr>
            <b-tr v-if="plan.length === 0">
              <b-td colspan="6" class="text-center text-muted kl-ai-small">
                {{
                  $t("reusable.aiQuestionsBuilderModal.planEmpty", {
                    entity: criteriaLabel,
                  })
                }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>

        <div class="d-flex align-items-center justify-content-between">
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            :disabled="busy || criteriaOptions.length === 0"
            @click="addRow"
          >
            <b-icon icon="plus" class="mr-1"></b-icon>
            {{ $t("reusable.aiQuestionsBuilderModal.addRow") }}
          </b-button>
          <small :class="scoreMismatch ? 'text-danger' : 'text-muted'">
            {{
              testScore > 0
                ? $t("reusable.aiQuestionsBuilderModal.planTotalOf", {
                    total: plannedScore,
                    score: testScore,
                  })
                : $t("reusable.aiQuestionsBuilderModal.planTotal", {
                    total: plannedScore,
                  })
            }}
          </small>
        </div>

        <!-- Lo que la situación evaluativa no habilita no se esconde: se dice.
             Si no, el docente busca un tipo de pregunta que no está y no
             entiende por qué. -->
        <p v-if="disabledTypeLabels.length > 0" class="kl-ai-small text-muted mt-2 mb-0">
          <b-icon icon="info-circle" class="mr-1"></b-icon>
          {{
            $t("reusable.aiQuestionsBuilderModal.typesDisabled", {
              types: disabledTypeLabels.join(", "),
              situation: situationName,
            })
          }}
        </p>
      </template>

      <!-- ===================== Paso 2 · Revisión ===================== -->
      <template v-else>
        <p class="kl-ai-small text-muted mb-3">
          <b-spinner
            v-if="generating"
            small
            variant="primary"
            class="mr-1"
          ></b-spinner>
          {{
            generating
              ? $t("reusable.aiQuestionsBuilderModal.streaming", {
                  done: proposed.length,
                  total: plan.length,
                })
              : $t("reusable.aiQuestionsBuilderModal.reviewHint", {
                  count: pendingItems.length,
                })
          }}
        </p>

        <div
          v-for="(question, index) in proposed"
          :key="`proposed-${question.ref}`"
          class="kl-ai-question mb-3"
          :class="{ 'kl-ai-question-discarded': question.discarded }"
        >
          <div class="d-flex align-items-start justify-content-between">
            <div class="kl-ai-small text-muted">
              <strong>{{ index + 1 }}</strong>
              · {{ typeLabel(question.type) }}
              ·
              <template v-if="question.dimension_label">
                {{ dimensionLabel }} {{ question.dimension_label }}
              </template>
              <template v-else>
                {{ criteriaLabel }} {{ question.criteria_label }}
              </template>
              ·
              {{
                $t("reusable.aiQuestionsBuilderModal.points", {
                  score: question.max_score,
                })
              }}
            </div>
            <div class="d-flex align-items-center">
              <!-- Regenerar sólo esta pregunta, conservando su fila del plan:
                   mismo Indicador, mismo tipo, mismo puntaje. Es el equivalente
                   del botón de cada tarjeta del Diseño Instruccional. -->
              <b-button
                v-if="!question.discarded"
                variant="link"
                size="sm"
                class="p-0 mr-2 kl-ai-row-remove"
                :disabled="busy || redoing_ref != null"
                :title="$t('reusable.aiQuestionsBuilderModal.redo')"
                @click="redoOne(question)"
              >
                <b-spinner
                  v-if="redoing_ref === question.ref"
                  small
                  variant="primary"
                ></b-spinner>
                <b-icon v-else icon="arrow-repeat"></b-icon>
              </b-button>
              <b-button
                variant="link"
                size="sm"
                class="p-0 kl-ai-row-remove"
                :disabled="busy"
                :title="
                  question.discarded
                    ? $t('reusable.aiQuestionsBuilderModal.restore')
                    : $t('reusable.aiQuestionsBuilderModal.discard')
                "
                @click="toggleDiscard(question)"
              >
                <b-icon
                  :icon="question.discarded ? 'arrow-counterclockwise' : 'trash'"
                ></b-icon>
              </b-button>
            </div>
          </div>

          <div class="kl-ai-question-title mt-1">{{ question.title }}</div>
          <p v-if="question.description" class="kl-ai-small mb-1">
            {{ question.description }}
          </p>

          <!-- La correcta marcada y con su puntaje: es lo que hay que poder
               revisar antes de guardar. El puntaje lo repartió el backend
               entre las correctas; la IA no lo calcula. -->
          <ul v-if="question.options" class="kl-ai-options mb-1">
            <li
              v-for="option in question.options"
              :key="`option-${question.ref}-${option.order}`"
              :class="{ 'kl-ai-option-correct': option.is_correct }"
            >
              <b-icon
                :icon="option.is_correct ? 'check-circle-fill' : 'circle'"
                class="mr-1"
              ></b-icon>
              {{ option.title }}
              <span v-if="option.is_correct" class="kl-ai-small text-muted">
                ({{ option.score }})
              </span>
            </li>
          </ul>
          <p
            v-if="question.max_answer_length"
            class="kl-ai-small text-muted mb-0"
          >
            {{
              $t("reusable.aiQuestionsBuilderModal.maxWords", {
                words: question.max_answer_length,
              })
            }}
          </p>
        </div>

        <!-- Una fila del plan que no volvió se dice. Antes faltaba la
             pregunta y no había nada en pantalla que lo explicara: el docente
             pedía tres tipos y contaba dos preguntas. -->
        <p
          v-if="!generating && missingCount > 0"
          class="kl-ai-small text-danger mb-0"
        >
          <b-icon icon="exclamation-triangle" class="mr-1"></b-icon>
          {{
            $t("reusable.aiQuestionsBuilderModal.missingRows", {
              count: missingCount,
            })
          }}
        </p>

        <p
          v-if="!generating && proposed.length === 0"
          class="text-center text-muted kl-ai-small"
        >
          {{ $t("reusable.aiQuestionsBuilderModal.reviewEmpty") }}
        </p>
      </template>

      <template #modal-footer>
        <div class="d-flex align-items-center justify-content-end w-100">
          <small v-if="saving" class="text-muted mr-auto">
            <b-spinner small variant="primary" class="mr-1"></b-spinner>
            {{
              $t("reusable.aiQuestionsBuilderModal.savingProgress", {
                done: saved_count,
                total: pendingItems.length,
              })
            }}
          </small>
          <b-button
            variant="secondary"
            size="sm"
            class="mr-2"
            :disabled="busy"
            @click="step === 'review' && !generating ? back() : close()"
          >
            {{
              step === "review" && !generating
                ? $t("reusable.aiQuestionsBuilderModal.back")
                : $t("reusable.aiQuestionsBuilderModal.close")
            }}
          </b-button>
          <b-button
            v-if="step === 'setup'"
            variant="primary"
            size="sm"
            :disabled="!canGenerate"
            @click="generate"
          >
            <img src="@/assets/icon-ai.svg" alt="" class="kl-ai-icon-sm mr-1" />
            {{
              $t("reusable.aiQuestionsBuilderModal.generate", {
                count: plan.length,
              })
            }}
          </b-button>
          <b-button
            v-else
            variant="primary"
            size="sm"
            :disabled="busy || pendingItems.length === 0"
            @click="save"
          >
            {{
              $t("reusable.aiQuestionsBuilderModal.save", {
                count: pendingItems.length,
              })
            }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </span>
</template>

<script>
// E54 · Generar con IA las preguntas de un Instrumento, desde lo que la pieza
// del Diseño Instruccional ya declaró.
//
// Dos pasos, como los demás modales de creación rápida: el PLAN (qué preguntas,
// sobre qué Indicador, de qué tipo) y la REVISIÓN de lo que la IA redactó. Nada
// se guarda hasta el botón del paso 2, y lo que ya existe en el instrumento no
// se toca nunca: las aceptadas se agregan al final.
//
// El plan va armado desde acá y es autoritativo en el backend. Es la misma
// decisión que tomó el builder del Diseño Instruccional con su línea de tiempo:
// una lista no se puede incumplir, un pedido de cantidades sí.
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";
import useAiBuilderStream from "@/components/reusable/ai-builder/useAiBuilderStream";
import { saveAiQuestions } from "@/components/new-evaluations/Evaluation/Questions/createAiQuestions";

// Cuántas filas trae el plan al abrirse.
//
// Tres, fijo, y no una por Indicador de Logro: una evaluación de cierre de curso
// declara todos los Indicadores de la asignatura, y proponer veinte preguntas
// deja al usuario borrando filas antes de poder trabajar —y a un clic de
// distancia de generar veinte. Tres es un punto de partida que se lee de una
// vez; agregar la cuarta es una fila más.
const DEFAULT_PLAN_ROWS = 3;

// Los tipos que este builder sabe escribir, con el campo de la Situación
// Evaluativa que los habilita. Son los mismos tres que ofrece el formulario
// manual: generar uno que el formulario no edita dejaría preguntas que después
// nadie puede corregir a mano.
const TYPES = [
  {
    key: "redaction",
    flag: "enable_redaction_questions",
    label_key: "typeRedaction",
  },
  {
    key: "simple_selection",
    flag: "enable_simple_selection_questions",
    label_key: "typeSimpleSelection",
  },
  {
    key: "multiple_selection",
    flag: "enable_multiple_selection_questions",
    label_key: "typeMultipleSelection",
  },
];

export default {
  name: "AiQuestionsBuilderModal",
  components: {
    AiThemeInput: () =>
      import("@/components/reusable/ai-builder/AiThemeInput"),
  },
  props: {
    test_id: {
      type: Number,
      required: true,
    },
    // El Segmento desde el que se abrió, si la zona pertenece a uno: las
    // preguntas nacen dentro de él, igual que al agregarlas a mano.
    segment_id: {
      type: Number,
      default: null,
    },
    // Los Indicadores de Logro que la pieza del diseño declaró. De acá sale el
    // plan por defecto: una pregunta por Indicador.
    evaluation_criteria_micro_ids: {
      type: Array,
      default: () => [],
    },
    // El `order` de la primera pregunta nueva: al final de lo que ya existe.
    next_order: {
      type: Number,
      default: 1,
    },
    // El nombre de la pieza del diseño, sólo para mostrarlo en el paso 1.
    piece_name: {
      type: String,
      default: "",
    },
    // La Situacion Evaluativa llega como prop y no del store porque no tiene
    // getter: la vista de la evaluacion la trae una vez y la pasa hacia abajo,
    // y este modal se cuelga del mismo lugar que el boton de agregar pregunta.
    evaluation_situations: {
      type: Array,
      default: () => [],
    },
    show_button: {
      type: Boolean,
      default: true,
    },
    button_class: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      visible: false,
      step: "setup",
      theme: "",
      plan: [],
      proposed: [],
      generating: false,
      saving: false,
      saved_count: 0,
      error: null,
      uid: 0,
      // El plan con el que se generó, indexado por `ref`. Se guarda al generar
      // y no se recalcula: regenerar una pregunta tiene que mandar la MISMA
      // fila, no la que el plan tenga después de que alguien lo edite.
      plan_by_ref: {},
      redoing_ref: null,
      // Lo pone `save` y lo lee `confirmClose`: el cierre que viene DESPUÉS de
      // guardar no tiene nada que perder, así que no pregunta.
      skip_close_confirm: false,
    };
  },
  computed: {
    ...mapGetters({
      tests: names.NEW_TESTS,
      evaluation_criteria_micros: names.EVALUATION_CRITERIAS,
      study_units: names.STUDY_UNITS,
      competence_units: names.COMPETENCE_UNITS,
      rubrics: names.NEW_RUBRIC_INSTRUMENTS,
      rubric_criterias: names.NEW_RUBRIC_CRITERIAS,
      redaction_questions: names.NEW_REDACTION_QUESTIONS,
      multiple_choice_questions: names.NEW_MULTIPLE_CHOICE_QUESTIONS,
    }),
    busy() {
      return this.generating || this.saving || this.redoing_ref != null;
    },
    // Las filas del plan que la generación no devolvió. `plan_by_ref` es el
    // plan con el que se generó, no el que esté en pantalla después.
    missingCount() {
      const planned = Object.keys(this.plan_by_ref).length;
      if (planned === 0) return 0;
      return Math.max(0, planned - this.proposed.length);
    },
    test() {
      return this.tests.find((x) => x.id == this.test_id) || null;
    },
    testTitle() {
      return this.test ? this.test.title : "";
    },
    testScore() {
      return this.test ? parseFloat(this.test.max_score) || 0 : 0;
    },
    evaluationSituation() {
      if (!this.test || this.test.evaluation_situation == null) return null;
      return (
        this.evaluation_situations.find(
          (x) => x.id == this.test.evaluation_situation
        ) || null
      );
    },
    situationName() {
      return this.evaluationSituation ? this.evaluationSituation.name : "";
    },
    pieceName() {
      return this.piece_name;
    },
    instrumentLabel() {
      return this.$getVisibleNames("evaluations2.test", false, "Instrumento");
    },
    criteriaLabel() {
      return this.$getVisibleNames(
        "teaching.evaluationcriteriamicro",
        false,
        "Indicador de Logro"
      );
    },
    dimensionLabel() {
      return this.$getVisibleNames("manual.dimension", false, "Dimensión");
    },
    questionsLabel() {
      return this.$t("reusable.aiQuestionsBuilderModal.questionsEntity");
    },
    // Los tipos que la situación evaluativa habilita. Sin situación no hay
    // restricción, mismo criterio que el formulario de preguntas.
    availableTypes() {
      const situation = this.evaluationSituation;
      if (!situation) return TYPES;
      if (!situation.enable_questions) return [];
      return TYPES.filter((type) => situation[type.flag]);
    },
    typeOptions() {
      return this.availableTypes.map((type) => ({
        value: type.key,
        text: this.$t(`reusable.aiQuestionsBuilderModal.${type.label_key}`),
      }));
    },
    disabledTypeLabels() {
      if (!this.evaluationSituation) return [];
      const available = this.availableTypes.map((x) => x.key);
      return TYPES.filter((type) => !available.includes(type.key)).map((type) =>
        this.$t(`reusable.aiQuestionsBuilderModal.${type.label_key}`)
      );
    },
    criteriaOptions() {
      return this.evaluation_criteria_micros
        .filter((x) => this.evaluation_criteria_micro_ids.includes(x.id))
        .map((criteria) => {
          const study_unit = this.study_units.find(
            (x) => x.id == criteria.study_unit
          );
          // Tres niveles —Unidad de Competencia, Unidad de Estudio,
          // Indicador—, que es como lo numeran el formulario manual
          // (`RubricCriteriaForm.vue`) y la pauta ya creada. Con dos niveles el
          // modal decía "1.1" donde la pauta dice "1.1.1" y el docente no
          // reconocía el Indicador que había elegido. Sin Unidad de Competencia
          // quedan dos, el mismo respaldo que hace la vista.
          const competence_unit = study_unit
            ? this.competence_units.find(
                (x) => x.id == study_unit.temp_competence_unit
              )
            : null;
          const unit_prefix = competence_unit
            ? `${competence_unit.order}.${study_unit.order}`
            : study_unit
            ? `${study_unit.order}`
            : "";
          const prefix = unit_prefix
            ? `${unit_prefix}.${criteria.order}`
            : `${criteria.order}`;
          return {
            id: criteria.id,
            prefix,
            study_unit_id: study_unit ? study_unit.id : null,
            competence_unit_order: competence_unit
              ? competence_unit.order
              : 999,
            study_unit_order: study_unit ? study_unit.order : 999,
            study_unit_prefix: unit_prefix,
            study_unit_name: study_unit ? study_unit.name : "",
            label: `${prefix} ${criteria.full_sentence}`,
          };
        })
        .sort((a, b) => {
          if (a.competence_unit_order !== b.competence_unit_order)
            return a.competence_unit_order - b.competence_unit_order;
          if (a.study_unit_order !== b.study_unit_order)
            return a.study_unit_order - b.study_unit_order;
          return a.label.localeCompare(b.label);
        });
    },
    // Los Indicadores agrupados por Unidad de Estudio: el selector del
    // formulario de preguntas los muestra así, y una lista plana de treinta
    // oraciones largas no se puede leer.
    criteriaGroups() {
      const groups = [];
      for (const criteria of this.criteriaOptions) {
        let group = groups.find((x) => x.id === criteria.study_unit_id);
        if (!group) {
          group = {
            id: criteria.study_unit_id,
            label: criteria.study_unit_name
              ? `${criteria.study_unit_prefix}: ${criteria.study_unit_name}`
              : this.$getVisibleNames(
                  "teaching.studyunit",
                  false,
                  "Unidad de Estudio"
                ),
            criterias: [],
          };
          groups.push(group);
        }
        group.criterias.push(criteria);
      }
      return groups;
    },
    // El alto del selector múltiple: muestra hasta cuatro Indicadores sin
    // obligar a desplazarse, y no crece más allá para que la tabla se siga
    // leyendo cuando la asignatura declara veinte.
    criteriaSelectSize() {
      return Math.min(4, Math.max(2, this.criteriaOptions.length));
    },
    // Hay trabajo que perder cuando la IA ya escribió algo, cuando está
    // escribiendo, o cuando se redactó un tema a mano. El plan por sí solo no
    // cuenta: se vuelve a armar solo al reabrir.
    hasUnsavedWork() {
      return this.generating || this.proposed.length > 0 || !!this.theme;
    },
    plannedScore() {
      return this.$round(
        this.plan.reduce((total, row) => total + (row.max_score || 0), 0)
      );
    },
    // El puntaje planificado contra el del Instrumento. Se avisa, no se
    // bloquea: el docente puede estar agregando preguntas a un instrumento que
    // después va a repuntuar, y el formulario manual ofrece ajustarlo al final.
    scoreMismatch() {
      if (this.testScore <= 0) return false;
      return this.plannedScore + this.existingScore > this.testScore;
    },
    existingScore() {
      const sum = (list) =>
        list
          .filter((x) => x.test == this.test_id)
          .reduce((total, x) => total + (parseFloat(x.max_score) || 0), 0);
      return this.$round(
        sum(this.redaction_questions) + sum(this.multiple_choice_questions)
      );
    },
    // Las preguntas que el instrumento ya tiene, de los dos tipos que este
    // builder escribe.
    existingQuestions() {
      return [
        ...this.redaction_questions.filter((x) => x.test == this.test_id),
        ...this.multiple_choice_questions.filter((x) => x.test == this.test_id),
      ];
    },
    existingCount() {
      return this.existingQuestions.length;
    },
    // Cuántas preguntas mide ya cada Indicador. Es lo que convierte "generar de
    // nuevo" en "generar lo que falta".
    criteriaCoverage() {
      // Donde se evalúa a través de la Dimensión la cobertura se cuenta por
      // dimensión: las preguntas no declaran Indicadores, así que contarlas por
      // Indicador diría "sin preguntas" en todas y el modal propondría de nuevo
      // lo que ya existe.
      if (this.dimensionDriven)
        return this.dimensionOptions.map((dimension) => ({
          id: dimension.id,
          prefix: dimension.label.split(".")[0] + ".",
          count: this.existingQuestions.filter(
            (question) => question.linked_rubric_criteria == dimension.id
          ).length,
        }));
      return this.criteriaOptions.map((criteria) => ({
        id: criteria.id,
        prefix: criteria.prefix,
        // Una pregunta que mide dos Indicadores cuenta para los dos: es lo que
        // hace que la cobertura diga la verdad.
        count: this.existingQuestions.filter((question) =>
          (question.evaluation_criterias_micro || []).includes(criteria.id)
        ).length,
      }));
    },
    uncoveredCriteriaIds() {
      return this.criteriaCoverage
        .filter((coverage) => coverage.count === 0)
        .map((coverage) => coverage.id);
    },
    pendingItems() {
      return this.proposed.filter((x) => !x.discarded);
    },
    /**
     * La institución evalúa a través de la Dimensión.
     *
     * En DUOC la pregunta NO declara Indicadores —su selector está escondido
     * en `NewQuestionForm.vue`—: se cuelga de una Dimensión de la pauta, y es
     * la dimensión la que mide el Indicador. Así que el plan elige dimensión.
     */
    dimensionDriven() {
      return !!(
        this.institution && this.institution.internal_use_id == "duoc.cl"
      );
    },
    // Las Dimensiones de la pauta de este Instrumento, en su orden.
    dimensionOptions() {
      const rubric_ids = this.rubrics
        .filter((x) => x.test == this.test_id)
        .map((x) => x.id);
      if (rubric_ids.length === 0) return [];
      return this.rubric_criterias
        .filter((x) => rubric_ids.includes(x.rubric))
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((criteria) => ({
          id: criteria.id,
          label: `${criteria.order}. ${criteria.full_sentence}`,
        }));
    },
    canGenerate() {
      if (this.busy || this.plan.length === 0) return false;
      // Donde se evalúa a través de la Dimensión, lo que la fila necesita es
      // su dimensión; en el resto, su Indicador. Sin eso el backend la
      // descartaría en silencio: mejor no dejar generar.
      if (this.dimensionDriven)
        return this.plan.every((row) => row.dimension != null);
      return this.plan.every((row) => (row.criterias || []).length > 0);
    },
  },
  methods: {
    /**
     * Las Dimensiones de la pauta de este Instrumento, traídas al store.
     *
     * Hace falta esperar: el plan se arma con ellas, y el modal puede abrirse
     * sin que nadie haya entrado a la pauta todavía —la pauta y las preguntas
     * son dos contenedores distintos y cada uno trae lo suyo—.
     */
    async loadDimensions() {
      const rubrics = await this.$store.dispatch(
        names.FETCH_NEW_RUBRIC_INSTRUMENTS,
        { test_id: this.test_id }
      );
      for (const rubric of rubrics || [])
        await this.$store.dispatch(names.FETCH_NEW_RUBRIC_CRITERIAS, rubric.id);
    },
    async open() {
      this.reset();
      if (this.availableTypes.length === 0) {
        toast(
          this.$t("reusable.aiQuestionsBuilderModal.noTypesAvailable", {
            situation: this.situationName,
          })
        );
        return;
      }
      // Donde se evalúa a través de la Dimensión lo que hace falta son las
      // dimensiones de la pauta: sin pauta no hay de dónde colgar la pregunta,
      // y se dice en vez de abrir un paso 1 que no se puede completar.
      if (this.dimensionDriven) {
        await this.loadDimensions();
        if (this.dimensionOptions.length === 0) {
          toast(
            this.$t("reusable.aiQuestionsBuilderModal.noDimensions", {
              entity: this.dimensionLabel,
            })
          );
          return;
        }
      } else if (this.criteriaOptions.length === 0) {
        toast(
          this.$t("reusable.aiQuestionsBuilderModal.noCriterias", {
            entity: this.criteriaLabel,
          })
        );
        return;
      }
      // Plan por defecto: tres filas, sobre los Indicadores que TODAVÍA no
      // tienen pregunta. Los sin cubrir van primero porque son lo que falta
      // medir; si ya están todos cubiertos, una fila suelta sobre el primero,
      // para que agregar una más siga siendo el gesto.
      if (this.dimensionDriven) {
        // La misma regla que con Indicadores: primero las dimensiones que
        // TODAVÍA no tienen pregunta, porque son lo que falta medir.
        const pending_dimensions = this.uncoveredCriteriaIds;
        const seed_dimensions =
          pending_dimensions.length > 0
            ? pending_dimensions.slice(0, DEFAULT_PLAN_ROWS)
            : [this.dimensionOptions[0].id];
        this.plan = seed_dimensions.map((dimension_id) => {
          const row = this.newRow(null);
          row.dimension = dimension_id;
          return row;
        });
        this.visible = true;
        return;
      }
      const pending = this.uncoveredCriteriaIds;
      const seed =
        pending.length > 0
          ? pending.slice(0, DEFAULT_PLAN_ROWS)
          : [this.criteriaOptions[0].id];
      this.plan = seed.map((criteria_id) => this.newRow(criteria_id));
      this.visible = true;
    },
    close() {
      this.visible = false;
    },
    back() {
      this.step = "setup";
      this.proposed = [];
      this.error = null;
    },
    reset() {
      this.step = "setup";
      this.theme = "";
      this.plan = [];
      this.proposed = [];
      this.generating = false;
      this.saving = false;
      this.saved_count = 0;
      this.error = null;
      this.plan_by_ref = {};
      this.redoing_ref = null;
      this.skip_close_confirm = false;
    },
    /**
     * Pregunta antes de cerrar cuando hay algo que perder.
     *
     * Un clic fuera del modal cerraba el paso 2 con las preguntas ya escritas y
     * sin guardar, y no había forma de recuperarlas: hay que generarlas de
     * nuevo, con su costo. Mismo gesto y mismos textos que la Matriz y el
     * formulario de la asignatura, para que la plataforma pregunte siempre igual.
     */
    confirmClose(event) {
      if (this.skip_close_confirm || !this.hasUnsavedWork) return;
      event.preventDefault();
      this.$bvModal
        .msgBoxConfirm(
          this.$t("evaluations.newEvaluations.sureCloseUnsavedData"),
          {
            title: this.$t("evaluations.newEvaluations.confirmationRequired"),
            size: "md",
            buttonSize: "md",
            okVariant: "warning",
            okTitle: this.$t("evaluations.newEvaluations.yesClose"),
            cancelTitle: this.$t("reusable.common.cancel"),
            footerClass: "p-2",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (!value) return;
          this.skip_close_confirm = true;
          this.visible = false;
        });
    },
    newRow(criteria_id) {
      const type = (this.availableTypes[0] || {}).key || "redaction";
      this.uid += 1;
      return {
        uid: this.uid,
        // Una lista, aunque casi siempre traiga uno: es lo que deja declarar una
        // pregunta que mide dos Indicadores a la vez.
        criterias: criteria_id != null ? [criteria_id] : [],
        // Sólo donde se evalúa a través de la Dimensión. En el resto queda en
        // null y el backend ni la mira.
        dimension: null,
        type,
        max_score: 1,
        options_count: 4,
        correct_count: 2,
      };
    },
    addRow() {
      if (this.dimensionDriven) {
        const first = this.dimensionOptions[0];
        if (!first) return;
        const row = this.newRow(null);
        row.dimension = first.id;
        this.plan.push(row);
        return;
      }
      const first = this.criteriaOptions[0];
      if (!first) return;
      this.plan.push(this.newRow(first.id));
    },
    removeRow(uid) {
      this.plan = this.plan.filter((row) => row.uid !== uid);
    },
    isSelectionType(type) {
      return type === "simple_selection" || type === "multiple_selection";
    },
    typeLabel(type) {
      const found = TYPES.find((x) => x.key === type);
      if (!found) return "";
      return this.$t(`reusable.aiQuestionsBuilderModal.${found.label_key}`);
    },
    toggleDiscard(question) {
      this.$set(question, "discarded", !question.discarded);
    },
    async generate() {
      this.error = null;
      this.proposed = [];
      this.step = "review";
      this.generating = true;
      const { callBuilder } = useAiBuilderStream(
        {
          url: "/common/evaluation-instrument-builder-ai/",
          generate_error_key: "reusable.aiQuestionsBuilderModal.generateError",
        },
        this
      );
      // El `ref` lo asigna el backend por la posición de la fila, así que esta
      // foto es la que después permite regenerar una sola con su misma fila.
      this.plan_by_ref = {};
      this.plan.forEach((row, index) => {
        this.plan_by_ref[index + 1] = row;
      });
      try {
        const result = await callBuilder(
          {
            test: this.test_id,
            target: "questions",
            theme: this.theme || "",
            plan: this.plan.map((row) => this.rowPayload(row)),
          },
          // Cada pregunta aparece apenas la IA la cierra: con seis preguntas de
          // selección la espera es larga y una pantalla quieta parece colgada.
          (event) => {
            if (event.type === "question" && event.item) {
              this.proposed.push({ ...event.item, discarded: false });
            }
          }
        );
        // El `done` es autoritativo: reemplaza lo que llegó en vivo, que pudo
        // incluir una pregunta que el validador final descartó.
        this.proposed = ((result || {}).questions || []).map((question) => ({
          ...question,
          discarded: false,
        }));
        if (this.proposed.length === 0) {
          this.error = this.$t(
            "reusable.aiQuestionsBuilderModal.generateEmpty"
          );
        }
      } catch (error) {
        this.error =
          error.message ||
          this.$t("reusable.aiQuestionsBuilderModal.generateError");
      } finally {
        this.generating = false;
      }
    },
    rowPayload(row) {
      return {
        type: row.type,
        evaluation_criterias_micro: row.criterias,
        linked_rubric_criteria: row.dimension,
        max_score: row.max_score,
        options_count: row.options_count,
        correct_count: row.correct_count,
      };
    },
    /**
     * Regenera UNA de las preguntas propuestas, conservando su fila del plan.
     *
     * Reemplaza lo que la pregunta dice y no lo que la ubica: mismo Indicador,
     * mismo tipo, mismo puntaje. Es la misma política que `redoPieceWithAi` en
     * el Diseño Instruccional, y es lo que hace que regenerar no sea
     * destructivo: sin ella, pedir otra redacción podría mover la pregunta de
     * Indicador y con eso la cobertura del instrumento.
     *
     * Las demás propuestas no se tocan ni se vuelven a pedir: el pedido es de
     * una sola fila.
     */
    async redoOne(question) {
      const row = this.plan_by_ref[question.ref];
      if (!row || this.redoing_ref != null) return;
      this.redoing_ref = question.ref;
      this.error = null;
      const { callBuilder } = useAiBuilderStream(
        {
          url: "/common/evaluation-instrument-builder-ai/",
          generate_error_key: "reusable.aiQuestionsBuilderModal.generateError",
        },
        this
      );
      try {
        const result = await callBuilder({
          test: this.test_id,
          target: "questions",
          theme: this.theme || "",
          plan: [this.rowPayload(row)],
        });
        const fresh = ((result || {}).questions || [])[0];
        if (!fresh) {
          this.error = this.$t(
            "reusable.aiQuestionsBuilderModal.generateEmpty"
          );
          return;
        }
        // El `ref` que vuelve es 1 —el plan tenía una sola fila—, así que se le
        // devuelve el suyo para no perder su lugar en la lista ni su fila.
        const index = this.proposed.findIndex((x) => x.ref === question.ref);
        if (index >= 0) {
          this.proposed.splice(index, 1, {
            ...fresh,
            ref: question.ref,
            discarded: false,
          });
        }
      } catch (error) {
        this.error =
          error.message ||
          this.$t("reusable.aiQuestionsBuilderModal.generateError");
      } finally {
        this.redoing_ref = null;
      }
    },
    async save() {
      const questions = this.pendingItems;
      if (questions.length === 0) return;
      this.saving = true;
      this.saved_count = 0;
      try {
        const created = await saveAiQuestions(this, {
          // Donde se evalúa a través de la Dimensión, la pregunta guarda la
          // dimensión y NO el Indicador: es como están sus preguntas hechas a
          // mano, porque su formulario no ofrece el selector de Indicadores.
          dimension_driven: this.dimensionDriven,
          test_id: this.test_id,
          segment_id: this.segment_id,
          questions,
          start_order: this.next_order,
          on_saved: (count) => {
            this.saved_count = count;
          },
        });
        if (created.length === 0) {
          this.error = this.$t(
            "reusable.aiQuestionsBuilderModal.generateError"
          );
          return;
        }
        toast(
          this.$t("reusable.aiQuestionsBuilderModal.saved", {
            count: created.length,
          })
        );
        this.$emit("created", created);
        this.skip_close_confirm = true;
        this.visible = false;
        await this.askToUpdateTestScore();
      } finally {
        this.saving = false;
      }
    },
    /**
     * Si las preguntas pasaron el puntaje del Instrumento, ofrece subirlo.
     *
     * Es el mismo gesto que hace el formulario manual al crear una pregunta que
     * desborda el puntaje (`updateMaxScoreTest` en NewQuestionForm.vue), con sus
     * mismos textos: que una generación se comporte distinto a agregar la misma
     * pregunta a mano sería lo raro.
     */
    async askToUpdateTestScore() {
      const total = this.existingScore;
      if (this.testScore <= 0 || total <= this.testScore) return;
      const result = await this.$swal({
        title: `<p>${this.$t(
          "evaluations.newEvaluations.questionScoreDoesNotMatchInstrument",
          { entity: `<b>${this.instrumentLabel}</b>` }
        )}</p>`,
        text: this.$t(
          "evaluations.newEvaluations.shouldInstrumentScoreBeUpdatedAutomatically",
          { entity: this.instrumentLabel }
        ),
        confirmButtonText: this.$t("reusable.common.yes"),
        cancelButtonText: this.$t("reusable.common.no"),
        type: "info",
        showCancelButton: true,
      });
      if (!result.value) return;
      await this.$store.dispatch(names.PATCH_NEW_TEST, {
        new_test_id: this.test_id,
        item: { max_score: total },
      });
    },
  },
};
</script>

<style scoped>
/* El morado de la familia de botones de creación rápida: se leen como un
   mismo gesto, no como uno por vista. */
.btn-ai-quick {
  background-color: var(--kl-fourth-hover-color) !important;
  border-color: var(--kl-fourth-hover-color) !important;
  color: var(--kl-text-on-primary) !important;
}
.btn-ai-quick:hover {
  opacity: 0.9;
}
.kl-ai-icon-sm {
  width: 14px;
  height: 14px;
  filter: brightness(0) invert(1);
}
.kl-ai-icon-lg {
  width: 22px;
  height: 22px;
  margin-top: 2px;
}

/* El cuerpo y el pie los dibuja b-modal, así que su marcado no lleva el scope
   de este componente: sin ::v-deep estas reglas no llegarían. */
::v-deep .kl-ai-dialog {
  max-width: min(1100px, calc(100vw - 2rem));
}
::v-deep .kl-ai-body {
  max-height: 70vh;
  overflow-y: auto;
  text-align: left;
}
::v-deep .kl-ai-footer {
  background: var(--kl-surface-muted);
}

.kl-ai-title {
  font-weight: var(--kl-fw-bold);
  color: var(--kl-text-heading);
}
.kl-ai-close {
  color: var(--kl-text-muted);
  line-height: 1;
}
.kl-ai-steps {
  display: flex;
  gap: 4px;
}
.kl-ai-step {
  height: 3px;
  flex: 1;
  border-radius: var(--kl-radius-pill);
  background: var(--kl-border-soft);
  transition: background 0.2s ease;
}
.kl-ai-step-done {
  background: var(--kl-color-primary);
}
.kl-ai-label {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-heading);
}
.kl-ai-small {
  font-size: var(--kl-fs-small);
}
.kl-ai-context {
  border-left: 3px solid var(--kl-color-primary);
  padding-left: 0.6rem;
}
.kl-ai-table ::v-deep th {
  font-size: var(--kl-fs-small);
  font-weight: var(--kl-fw-semibold);
  background: var(--kl-surface-muted);
}
.kl-ai-row-remove {
  color: var(--kl-text-muted);
}
.kl-ai-existing {
  border-left: 3px solid var(--kl-border-soft);
  padding-left: 0.6rem;
}
.kl-ai-chip {
  display: inline-block;
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-pill);
  padding: 0 0.5rem;
  margin: 0 0.25rem 0.25rem 0;
  background: var(--kl-surface-muted);
}
.kl-ai-chip-empty {
  border-style: dashed;
  background: transparent;
  color: var(--kl-text-muted);
}
.kl-ai-question {
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-md);
  padding: 0.6rem 0.75rem;
}
.kl-ai-question-discarded {
  opacity: 0.45;
}
.kl-ai-question-title {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-heading);
}
.kl-ai-option-correct {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-heading);
}
.kl-ai-options {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  font-size: var(--kl-fs-small);
}
</style>
